import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { BiCardView } from '@euler/model/bi/dashboard';
import { GripVertical } from 'lucide-react-native';
import { memo, useMemo, useRef, useState } from 'react';
import { View, ViewStyle } from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
} from 'react-native-reanimated';
import Svg, { Path } from 'react-native-svg';
import { BiRect } from '../../functions/biGridGeometry';
import { useArrangesByTouch } from '../../functions/useArrangesByTouch';
import type { BiRenderCard } from './DashboardGrid';

/** A touch holds before it drags or resizes, so a flick on a handle scrolls. */
const kHoldMs = 250;
/** Room around a handle a finger may land in, past its drawn size. */
const kTouchSlop = 12;

const kResizeGrip = {
  position: 'absolute',
  right: 3,
  bottom: 3,
  width: 22,
  height: 22,
  alignItems: 'center',
  justifyContent: 'center',
  // With a mouse the pointer says resize; a touch reads the grip.
  cursor: 'nwse-resize',
} as ViewStyle;

export interface BiArrangeHandlers {
  /** The pointer moved by (dx, dy) since the gesture began. */
  onDragMove: (cardId: string, dx: number, dy: number) => void;
  onDrop: (cardId: string, dx: number, dy: number) => void;
  onResizeMove: (cardId: string, dx: number, dy: number) => void;
  onResizeEnd: (cardId: string, dx: number, dy: number) => void;
  /** The gesture was cancelled: nothing moves. */
  onCancel: () => void;
}

/**
 * A card on the editable grid: its grip drags it (the card follows by
 * transform), its corner resizes it (the grid redraws it at each new cell
 * size, not each pointer move). By touch, either starts after a short hold
 * on the handle, which then also takes a finger landing near it.
 */
export const ArrangeableItem = memo(
  ({
    card,
    rect,
    fits,
    onMeasure,
    renderCard,
    handlers,
  }: {
    card: BiCardView;
    rect: BiRect;
    /** Drawn at its content's height, `rect`'s at the least. */
    fits: boolean;
    /** A fitting card's content needs `px`, however tall it is drawn. */
    onMeasure: (cardId: string, px: number) => void;
    renderCard: BiRenderCard;
    handlers: BiArrangeHandlers;
  }) => {
    // The latest handlers, read by gestures built once: a gesture rebuilt
    // when the dashboard reloads mid-drag would cancel the drag.
    const latest = useRef(handlers);
    latest.current = handlers;
    const x = useSharedValue(0);
    const y = useSharedValue(0);
    const [active, setActive] = useState(false);
    const touch = useArrangesByTouch();
    // How a drag or resize begins: at once with a mouse, after a hold by
    // touch, when the dashboard's gesture-handler scroll view yields.
    const begin = useMemo(
      () => (pan: ReturnType<typeof Gesture.Pan>) =>
        touch
          ? pan.activateAfterLongPress(kHoldMs).hitSlop(kTouchSlop)
          : pan.minDistance(3),
      [touch],
    );
    const moved = useAnimatedStyle(() => ({
      transform: [{ translateX: x.value }, { translateY: y.value }],
    }));

    const drag = useMemo(
      () =>
        begin(Gesture.Pan().runOnJS(true))
          .onStart(() => setActive(true))
          .onUpdate(event => {
            x.value = event.translationX;
            y.value = event.translationY;
            latest.current.onDragMove(
              card.id,
              event.translationX,
              event.translationY,
            );
          })
          .onEnd((event, success) =>
            success
              ? latest.current.onDrop(
                  card.id,
                  event.translationX,
                  event.translationY,
                )
              : latest.current.onCancel(),
          )
          .onFinalize(() => {
            x.value = 0;
            y.value = 0;
            setActive(false);
          }),
      [begin, card.id, x, y],
    );
    const resize = useMemo(
      () =>
        begin(Gesture.Pan().runOnJS(true))
          .onUpdate(event =>
            latest.current.onResizeMove(
              card.id,
              event.translationX,
              event.translationY,
            ),
          )
          .onEnd((event, success) =>
            success
              ? latest.current.onResizeEnd(
                  card.id,
                  event.translationX,
                  event.translationY,
                )
              : latest.current.onCancel(),
          ),
      [begin, card.id],
    );

    const grip = (
      <GestureDetector gesture={drag}>
        <View
          accessibilityRole="button"
          accessibilityLabel={`拖动 ${card.title}`}
          accessibilityHint={touch ? '按住后拖动，移动这张卡片' : undefined}
          style={{ padding: 4, borderRadius: 6 }}
        >
          <GripVertical size={16} color={aiColors.mutedForeground} />
        </View>
      </GestureDetector>
    );
    const { height, ...at } = rect;
    return (
      <Animated.View
        style={[
          {
            position: 'absolute',
            ...at,
            ...(fits ? { minHeight: height } : { height }),
            zIndex: active ? 10 : 0,
          },
          active && { opacity: 0.92 },
          moved,
        ]}
      >
        {fits
          ? renderCard(card, undefined, grip, undefined, px =>
              onMeasure(card.id, px),
            )
          : renderCard(card, height, grip)}
        <GestureDetector gesture={resize}>
          <View
            accessibilityLabel={`${card.title} 的大小`}
            accessibilityHint={
              touch
                ? '按住后拖动，调整这张卡片的大小'
                : '拖动调整这张卡片的大小'
            }
            style={kResizeGrip}
          >
            {/* The resize grip of a window corner, not an expand arrow. */}
            <Svg width={12} height={12} viewBox="0 0 10 10">
              <Path
                d="M9 1 L1 9 M9 5 L5 9"
                stroke={aiColors.foreground}
                strokeOpacity={0.55}
                strokeWidth={1.5}
                strokeLinecap="round"
              />
            </Svg>
          </View>
        </GestureDetector>
      </Animated.View>
    );
  },
);
